import React from "react"
import { Box, BoxProps, Stack, useMediaQuery, useTheme } from "@carrotfertility/carotene-core"
import { HeaderLanguageSelector } from "../language-selector"
import { UserDropdown } from "../user-dropdown"
import { PartnerAccessAccountBanner } from "#/components/partner-access/PartnerAccessBanner"
import { useUserRole } from "#/components/partner-access/hooks/usePartnerAccess"
import { MOBILE_NAV_BLOCK_SIZE } from "#/components/views/app/navigation/dimensions"

type PageLayoutProps = {
  children: React.ReactNode | React.ReactNode[]
  footer?: React.ReactNode
  header?: React.ReactNode
  /**
   * Bound the page to the viewport and clip it, so its own content scrolls instead of the document.
   *
   * OPT-IN, default `false`: every other page in the app grows with its content and scrolls normally,
   * and none of them pass this.
   *
   * Messages needs it. Its two-column grid sizes the conversation list and the open thread with
   * `grid-template-rows: minmax(0, 1fr)`, which bounds nothing unless an ancestor has a height — and
   * this layout otherwise caps inline size only. The pane used to buy that bound itself with
   * `blockSize: calc(100vh - 216px)`, a constant encoding the pixel height of the chrome above it, which
   * went stale whenever that chrome changed and again when the tab bar arrived.
   *
   * The replacement carries NO such constant: one `100dvh` on the outer container, and the flex chain
   * below does the subtracting. `dvh` rather than `vh` so a mobile browser's collapsing toolbar does not
   * leave the composer under it.
   */
  fillViewport?: boolean
  /**
   * The header slot renders NOTHING in this state, so drop the space that separates it from the content.
   *
   * OPT-IN, default `false`. That space exists to hold a page's chrome — its title, its tab bar — off the
   * content below it. A page whose chrome steps aside entirely has nothing for it to separate, and the gap
   * shows: Messages drops its title and its tabs over an open provider conversation, which brings its own
   * header, and that header then sat 24px lower than the identical header on the Companion tab — where the
   * page's chrome IS the conversation's, and so sits above this margin rather than below it. Measured in
   * Chrome at 390×844: 36px under the navigation against 60px.
   *
   * NOT inferred from `header == null`, deliberately. Sixty-odd call sites pass no header at all and every
   * one of them wants this space; inferring the state would move all of them. This is for a page that HAS
   * chrome and is standing it down for one state.
   *
   * The space ABOVE the chrome is a separate margin on the header wrapper and is left alone — dropping that
   * too would pull the content up under the app's own navigation.
   */
  headerSlotEmpty?: boolean
} & (
  | { variant?: "sidebar"; sidebar?: React.ReactNode; sidebarDirection?: "start" | "end" }
  | { variant?: "form" }
  | { variant?: "default"; sx?: BoxProps["sx"] }
)

const footerAdjustmentStyles: Pick<BoxProps, "display" | "flexDirection" | "height"> = {
  display: "flex",
  flexDirection: "column",
  height: "100%"
}

/**
 * A shrinkable flex column that claims the space its siblings leave, for the `fillViewport` chain.
 *
 * `minBlockSize: 0` is the load-bearing half: a flex item's `min-height` defaults to `auto`, so without
 * it the item refuses to shrink below its content and the overflow escapes to the document — which is
 * the very thing the viewport bound exists to prevent.
 *
 * EXPORTED because the chain runs through a component this file does not own. Anything rendered between
 * the content box below and a page's own layout has to carry these four declarations too, or the chain
 * breaks at that element and every `flex: 1` beneath it is inert. Measured in Chrome, not theorised: a
 * styleless `<div>` in the middle left the messages grid at its content height — 2692px against a 1103px
 * viewport — with the composer clipped 1677px below the fold and the member unable to reply.
 */
export const FILL_REMAINING_SPACE = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minBlockSize: 0
} as const

function getMainComponentFromVariant(variant: "sidebar" | "default" | "form"): React.FC | null {
  switch (variant) {
    case "sidebar":
      return BodyWithSidebarLayout
    case "form":
      return FormLayout
    case "default":
    default:
      return null
  }
}

function FormLayout({ children }: { children: React.ReactNode | React.ReactNode[] }): JSX.Element {
  return <Box sx={{ maxInlineSize: "32rem" }}>{children}</Box>
}

export function BodyWithSidebarLayout({
  children,
  sidebar,
  direction = "end"
}: {
  direction?: "start" | "end"
  children: React.ReactNode | React.ReactNode[]
  sidebar: React.ReactNode
}): JSX.Element {
  const theme = useTheme()
  const switchToColumn = useMediaQuery(theme.breakpoints.down("lg"))
  function getDirection() {
    if (switchToColumn) {
      return direction === "end" ? "column" : "column-reverse"
    }
    return direction === "end" ? "row" : "row-reverse"
  }
  return (
    <Box
      gap={(theme) => theme.spacing(theme.tokens.spacing.xl)}
      display="flex"
      flexDirection={getDirection()}
      width="100%"
    >
      <Box width={switchToColumn ? "100%" : "66%"}>{children}</Box>
      <Box width={switchToColumn ? "100%" : "33%"}>{sidebar}</Box>
    </Box>
  )
}

export function PageLayout({
  footer,
  header,
  children,
  variant = "default",
  fillViewport = false,
  headerSlotEmpty = false,
  ...variantProps
}: PageLayoutProps) {
  const { isPartnerAccessingAccount } = useUserRole()
  const theme = useTheme()
  const showUserDropdown = useMediaQuery(theme.breakpoints.up("lg"))
  const mainComponent = getMainComponentFromVariant(variant)
  // Only the `default` variant accepts one; the others have no `sx` in their prop union.
  const variantSx = (variantProps as { sx?: BoxProps["sx"] }).sx
  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="center"
      marginX={(theme) => ({ xs: "16px", md: "32px", lg: theme.spacing(theme.tokens.spacing.xxxl) })}
      // The whole of the viewport bound, and the only place a viewport unit appears. Everything below
      // divides it up with flexbox rather than subtracting known heights from it.
      //
      // TWO VALUES, because this box does not start at the top of the viewport at every width:
      //
      //  • `lg` and up — the navigation is a fixed drawer BESIDE the content, so the box starts at the top
      //    of the screen and `100dvh` is exact.
      //  • below `lg` — the navigation is a header STACKED above `main` in normal flow (`MobileNav` in
      //    navigation/MainNavigation.tsx), so the box starts where that header ends and has to subtract
      //    it. A plain `100dvh` here made the document 100dvh + 96px and pushed the page's own last 96px
      //    off the bottom of the screen: reported on a phone, the conversation list ran past the fold and
      //    took `New message` with it.
      //
      // THE OFFSET IS IMPORTED, NOT MEASURED, and that is what separates it from the `calc(100vh - 216px)`
      // / `calc(100vh - 248px)` pair this replaced. Those summed the observed heights of several pieces of
      // this page's own chrome, so any one of them could move and leave the constant lying;
      // `MOBILE_NAV_BLOCK_SIZE` is the single value `MobileNav` pins its own height with, as both
      // `minHeight` and `maxHeight`. The header is exactly that tall, so the subtraction is exact, and if
      // it ever changes this follows. Nothing about the page's OWN chrome appears here — no header, no
      // banner, no tab bar; the flex chain below still does all of that.
      //
      // Leaving the page unbounded below `lg` was tried and rejected: it scrolled the document like an
      // ordinary page, but then a short conversation list stopped after its last row instead of filling
      // the screen, which is not what the panel should look like.
      sx={
        fillViewport
          ? {
              blockSize: { xs: `calc(100dvh - ${MOBILE_NAV_BLOCK_SIZE})`, lg: "100dvh" },
              overflow: "hidden"
            }
          : undefined
      }
    >
      <Box
        maxWidth={{ xl: "60.5rem", xs: "100vw" }}
        width="100%"
        {...(footer ? footerAdjustmentStyles : {})}
        // A shrinkable flex column, so the content box below can take the space the header leaves
        // instead of the page growing past the bound. `minBlockSize: 0` is the part that does the work:
        // a flex item defaults to `min-height: auto` and refuses to shrink below its content, which
        // pushes the overflow straight back out to the document.
        sx={fillViewport ? { display: "flex", flexDirection: "column", minBlockSize: 0 } : undefined}
      >
        {showUserDropdown ? (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              spacing={theme.spacing(theme.tokens.spacing.md)}
              sx={{ marginBlockStart: (theme) => theme.tokens.spacing.xl }}
            >
              <HeaderLanguageSelector />
              <UserDropdown />
            </Stack>
            {isPartnerAccessingAccount && <PartnerAccessAccountBanner />}
            {header}
          </>
        ) : (
          <Box marginTop={(theme) => theme.spacing(theme.tokens.spacing.xl)}>
            {isPartnerAccessingAccount && <PartnerAccessAccountBanner />}
            {header}
          </Box>
        )}
        <Box
          // The space between the page's chrome and its content — and NOTHING when the chrome renders
          // nothing, which is what `headerSlotEmpty` says. See that prop for what it is worth in pixels.
          marginTop={(theme) =>
            headerSlotEmpty
              ? 0
              : {
                  xs: theme.spacing(theme.tokens.spacing.lg),
                  md: theme.spacing(theme.tokens.spacing.xl)
                }
          }
          // `marginBlockEnd` through `sx` rather than the physical `marginBottom` system prop, per the
          // logical-properties rule. (`marginTop` above is pre-existing and left alone.)
          //
          // TWO MODES, because the margin means different things on either side of the bound. On an
          // ordinary scrolling page the 6rem below `md` is breathing room AFTER the end of the content.
          // Under the bound there is no "after": the space comes out of the panel, which is the very thing
          // being asked to fill the screen, and 6rem would hold it 96px clear of the bottom edge while its
          // sides sit 16px clear. So a bound page's bottom is an INSET MIRRORING `marginX` above, at the
          // same breakpoints and the same values, and the panel ends up evenly inset on all four sides.
          sx={(theme) => ({
            marginBlockEnd: fillViewport
              ? { xs: "16px", md: "32px", lg: theme.spacing(theme.tokens.spacing.xxxl) }
              : { xs: "6rem", md: theme.spacing(theme.tokens.spacing.xxxl) },
            // Takes whatever the header and the margins leave. This is where the subtraction the old
            // `calc(100vh - 216px)` did by hand actually happens, and flexbox does it.
            ...(fillViewport ? FILL_REMAINING_SPACE : {})
          })}
        >
          {/* `sx` last, after the spread, so the variant's own `sx` is merged rather than dropped —
              MUI takes an array and applies it in order. */}
          <Box
            component={mainComponent}
            {...variantProps}
            sx={fillViewport ? [variantSx, FILL_REMAINING_SPACE].flat() : variantSx}
          >
            {children}
          </Box>
        </Box>
        {footer}
      </Box>
    </Box>
  )
}
